import { useQuery } from '@tanstack/react-query';
import { Building2, Mail, ShieldOff, UserCog } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import { getDashboardCounts } from '@/lib/super-admin-api';

interface Stat {
  label: string;
  value: number | undefined;
  icon: typeof Building2;
  accent?: string;
}

export function SuperAdminDashboardPage() {
  const { error } = useToast();
  const {
    data,
    isLoading,
    isError,
    error: err,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['super-admin', 'dashboard'],
    queryFn: getDashboardCounts,
  });

  const stats: Stat[] = [
    { label: 'Organizations', value: data?.organizations, icon: Building2 },
    { label: 'Active orgs', value: data?.activeOrgs, icon: Building2, accent: 'text-emerald-600' },
    {
      label: 'Suspended orgs',
      value: data?.suspendedOrgs,
      icon: ShieldOff,
      accent: 'text-amber-600',
    },
    { label: 'Users', value: data?.users, icon: UserCog },
    { label: 'Pending invitations', value: data?.pendingInvitations, icon: Mail },
  ];

  return (
    <div className="mx-auto w-full max-w-6xl p-6">
      <PageHeader
        title="Super admin dashboard"
        description="Platform-wide overview and operator tools."
        actions={
          <Button asChild>
            <Link to="/super-admin/organizations?action=new">Create organization</Link>
          </Button>
        }
      />

      {isError ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-destructive">Could not load dashboard</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{extractApiErrorMessage(err)}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                refetch().catch((r) => error('Retry failed', extractApiErrorMessage(r)));
              }}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.label}
                </CardTitle>
                <stat.icon
                  className={`h-4 w-4 ${stat.accent ?? 'text-muted-foreground'}`}
                  aria-hidden
                />
              </CardHeader>
              <CardContent>
                {isLoading || (isFetching && stat.value === undefined) ? (
                  <Skeleton className="h-9 w-20" />
                ) : (
                  <div className={`text-3xl font-semibold tracking-tight ${stat.accent ?? ''}`}>
                    {stat.value ?? '—'}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-base">Getting started</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            1.{' '}
            <Link className="underline" to="/super-admin/organizations">
              Create an organization
            </Link>{' '}
            and invite its first admin by email.
          </p>
          <p>
            2. The admin accepts the invitation, logs in, and uses{' '}
            <span className="font-medium text-foreground">Settings → Invitations</span> to invite
            their team.
          </p>
          <p>
            3. Track outstanding admin invitations under{' '}
            <Link className="underline" to="/super-admin/invitations">
              Invitations
            </Link>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
